import { AxiosResponse } from 'axios'
import { JobStatus } from 'constants/API'
import { JobDTO } from 'models/misc'
import { useAPI } from 'utils/API'
import { logger } from 'utils/logging'
import { getMutation } from 'utils/reactQuery'

enum Endpoints {
  GET_JOB = '/job/{id}'
}

export enum JobPollErrorType {
  POLLING_RETRY_LIMIT = 'polling_retry_limit',
  JOB_FAILED = 'job_failed',
  BACKEND_ERROR = 'backend_error',
}

/** Enum of job types for logging and error tracking purposes */
export enum JobType {
  DEFAULT = 'Job',
  AI_ERASER = 'AI Eraser',
  VISUAL_STAGING = 'Visual Staging',
  WALL_PAINTING = 'Wall Painting',
  FLOOR_REPLACEMENT = 'Floor Replacement',
  VISUALS_EDITING = 'Visuals Editing',
  SELF_UPLOAD = 'Self Upload',
  MARKETING_MATERIAL = 'Marketing Material',
  DECLUTTER = 'Declutter',
  AI_ENHANCEMENT = 'AI Enhancement',
}

interface JobPollErrorResponse {
  response: {
    data: {
      status: number
      message: string
      timestamp: number
      errorType: JobPollErrorType
      backendError?: string
    }
  }
}

/**
 * Creates an Axios-like error response for job polling failures.
 * Mimics Axios error shape for uniform error handling in consumers.
 * @param errorType - The type of polling error that occurred
 * @param backendError - Optional backend error message to display
 */
const createJobPollErrorResponse = (errorType: JobPollErrorType, backendError?: string): JobPollErrorResponse => ({
  response: {
    data: {
      status: 400,
      message: errorType === JobPollErrorType.POLLING_RETRY_LIMIT ? 'polling_retry_limit_reached' : 'job_processing_failed',
      timestamp: new Date().getTime(),
      errorType,
      backendError
    }
  }
})

/**
 * Unified job polling hook for all async job operations.
 * Returns full JobDTO on success for maximum flexibility.
 * @param jobType - Type of job for logging purposes
 * @param maxRetries - Maximum polling attempts before timeout (default: 100)
 */
export function useJobPoll(jobType: JobType = JobType.DEFAULT, maxRetries: number = 100) {
  const api = useAPI<Endpoints>()

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  return getMutation<JobDTO, { jobId: string }>(
    async ({ jobId }) => {
      let retries = maxRetries
      let done = false
      let error = false
      let poll: AxiosResponse<JobDTO>

      do {
        poll = await api.get<JobDTO>(Endpoints.GET_JOB, { id: jobId }, true)

        if ((!poll.status || ![JobStatus.FINISHED, JobStatus.FAILED].includes(poll.data.status)) && retries-- > 0) {
          await delay(2000)
          continue
        }

        if (poll.data.status === JobStatus.FAILED || retries <= 0) error = true
        done = true
      } while (!done)

      if (error) {
        const backendError = poll?.data?.error || poll?.data?.message
        const isRetryLimitReached = retries <= 0
        const isJobFailed = poll.data.status === JobStatus.FAILED

        logger.error(`${jobType} job error: ${backendError || 'unknown_error'}`, new Error(`${jobType} job polling failed`), {
          context: {
            component: 'JobPoll',
            action: 'poll',
            metadata: {
              jobId,
              jobType,
              isRetryLimitReached,
              isJobFailed,
              retriesLeft: retries,
              backendError
            }
          }
        })

        if (backendError) {
          return Promise.reject(createJobPollErrorResponse(JobPollErrorType.BACKEND_ERROR, backendError))
        } else if (isRetryLimitReached) {
          return Promise.reject(createJobPollErrorResponse(JobPollErrorType.POLLING_RETRY_LIMIT))
        } else if (isJobFailed) {
          return Promise.reject(createJobPollErrorResponse(JobPollErrorType.JOB_FAILED))
        }
      }

      return Promise.resolve(poll.data)
    }
  )
}
